// What the post will look like, beside the box it is being written in.
//
// A representative preview, and deliberately not a facsimile: it shows the
// account it will go out as, the picture, the words and the network's own
// proportions, and it stops there. Redrawing Instagram's chrome pixel for pixel
// would be a trademark problem and a maintenance one — their UI changes, ours
// would silently start lying — and the question this card answers is "is my
// crop wrong and is my caption too long", which does not need their toolbar.
//
// Nothing here is read from a network. The avatar and the handle came back when
// the account was connected, and that is the only thing on this card that did.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AccountMark } from "../components/AccountMark";
import { PLATFORM_ICON } from "../components/PlatformIcons";
import { PLATFORM_LABEL, type Connection, type Platform } from "../lib/types";
import { useSharedComposer } from "./ComposerContext";

/** Where a caption is cut off in the feed, by network. Ours is a hint, not a
 *  limit — the post still publishes in full, and the counts are the platforms'
 *  published truncation points rather than their maximums. */
const TRUNCATE_AT: Record<Platform, number> = {
  instagram: 125,
  facebook: 250,
  youtube: 157,
};

/** `bare` drops the card chrome and the heading: on Compose this card sits
 *  inside a panel that a tab has already named, and a second "Post preview"
 *  heading under a tab reading "Preview" is a heading saying nothing twice. */
export function PostPreviewCard({ bare = false }: { bare?: boolean } = {}) {
  const c = useSharedComposer();
  const [tab, setTab] = useState<string | null>(null);

  // The chosen accounts, or — before anything is ticked — everything connected,
  // so the card is useful while the caption is still being written.
  const shown: Connection[] = c.selected.size > 0
    ? c.activeConnections.filter((x) => c.selected.has(x.id))
    : c.activeConnections;

  const current = shown.find((x) => x.id === tab) ?? shown[0] ?? null;

  // Un-ticking the account being previewed has to move the preview somewhere
  // real rather than leaving it on an account no longer in the post.
  useEffect(() => {
    if (tab && !shown.some((x) => x.id === tab)) setTab(shown[0]?.id ?? null);
  }, [shown, tab]);

  const cover = c.items[0];
  const empty = !cover && !c.caption.trim();

  return (
    <section className={bare ? "preview-card bare" : "card preview-card"}
             aria-labelledby={bare ? undefined : "preview-head"}
             aria-label={bare ? "Post preview" : undefined}>
      {!bare && (
        <div className="card-head">
          <div>
            <h2 id="preview-head">Post preview</h2>
            <p className="muted card-sub">How this looks on each network you have chosen.</p>
          </div>
        </div>
      )}

      {shown.length > 1 && (
        <div className="preview-tabs" role="tablist" aria-label="Preview network">
          {shown.map((x) => {
            const Icon = PLATFORM_ICON[x.platform];
            const on = current?.id === x.id;
            return (
              <button key={x.id} type="button" role="tab" aria-selected={on}
                      className={on ? "preview-tab on" : "preview-tab"}
                      title={`${PLATFORM_LABEL[x.platform]} — ${x.display_name}`}
                      onClick={() => setTab(x.id)}>
                <Icon className="platform-icon" />
                <span className="visually-hidden">
                  {PLATFORM_LABEL[x.platform]} — {x.display_name}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {empty ? (
        <p className="muted preview-empty">
          Your post preview will appear here as you create content.
        </p>
      ) : (
        <article className="preview-post" aria-live="polite">
          <header className="preview-who">
            {current
              ? <><AccountMark connection={current} size="md" />
                  <span className="preview-who-text">
                    <strong>{current.display_name}</strong>
                    <span className="muted">{PLATFORM_LABEL[current.platform]}</span>
                  </span></>
              : <span className="muted preview-who-none">
                  No account chosen yet — <Link to="/connections">connect one</Link> to see whose
                  name this goes out under.
                </span>}
          </header>

          {cover && (
            <div className={c.items.length > 1 ? "preview-media carousel" : "preview-media"}>
              {cover.previewUrl
                ? cover.facts.mimeType.startsWith("video/")
                  && !(cover.asset && cover.asset.preview_path)
                  ? <video src={cover.previewUrl} preload="metadata" muted playsInline />
                  : <img src={cover.previewUrl} alt="" />
                : <span className="thumb-fallback">🖼</span>}
              {c.items.length > 1 && (
                <span className="preview-count">1 / {c.items.length}</span>
              )}
            </div>
          )}

          {c.caption.trim() && (
            <PreviewCaption text={c.caption} platform={current?.platform ?? "instagram"} />
          )}
        </article>
      )}
    </section>
  );
}

/** The caption, cut where the network cuts it, with the rest still there. */
function PreviewCaption({ text, platform }: { text: string; platform: Platform }) {
  const [full, setFull] = useState(false);
  const limit = TRUNCATE_AT[platform];
  const long = text.length > limit;

  return (
    <p className="preview-caption">
      {full || !long ? text : `${text.slice(0, limit).trimEnd()}…`}
      {long && (
        <>
          {" "}
          <button type="button" className="linkish" onClick={() => setFull((on) => !on)}>
            {full ? "show less" : "more"}
          </button>
          <span className="muted preview-cut">
            {PLATFORM_LABEL[platform]} shows about the first {limit} characters before this fold.
            The whole caption still publishes.
          </span>
        </>
      )}
    </p>
  );
}
